
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { fetchPracticeSummary } from '../../store/slices/practiceSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserMd,
  faUsers,
  faUserNurse,
  faUserInjured,
  faCalendarAlt,
  faFileImport,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';

// Import components from PracticeManagement
import PracticeDetails from '../PracticeManagement/PracticeDetails/PracticeDetails';
import AdminManagement from '../PracticeManagement/AdminManagement/AdminManagement';
import ProviderManagement from '../PracticeManagement/ProviderManagement/ProviderManagement';
import CaregiverManagement from '../PracticeManagement/CaregiverManagement/CaregiverManagement';
import PatientManagement from '../PracticeManagement/PatientManagement/PatientManagement';
import SystemCaregiverManagement from '../PracticeManagement/SystemCaregiverManagement/SystemCaregiverManagement';
import ScheduleTargetSettings from '../PracticeManagement/ScheduleTargetSettings/ScheduleTargetSettings';
import ImportPatients from '../PracticeManagement/ImportPatients/ImportPatients';

import './ProviderDashboard.css';

/**
 * Local ErrorBoundary to avoid adding external deps.
 * Shows a visible fallback instead of leaving a blank area when a child throws.
 */
class LocalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // keep logging so devs can see stack traces in console
    console.error('[LocalErrorBoundary] caught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
    // optionally call parent's onReset if provided
    if (typeof this.props.onReset === 'function') {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.error) {
      const Fallback = this.props.FallbackComponent;
      // render user-provided fallback or minimal fallback
      if (Fallback) {
        return <Fallback error={this.state.error} resetErrorBoundary={this.reset} />;
      }
      return (
        <div style={{ padding: 12, border: '1px solid #e74c3c', background: '#fff5f5', borderRadius: 6 }}>
          <strong>Something went wrong.</strong>
          <div style={{ marginTop: 8, fontSize: 13 }}>{this.state.error?.message || 'Unknown error'}</div>
          <div style={{ marginTop: 8 }}>
            <button className="btn btn-sm btn-outline-secondary" onClick={this.reset}>Retry</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* Visible fallback UI for schedule tab errors */
const ScheduleErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div style={{
      border: '1px solid #e74c3c',
      background: '#fff5f5',
      color: '#a94442',
      padding: 12,
      borderRadius: 6,
      margin: 8
    }}>
      <strong>Schedule UI failed to render.</strong>
      <div style={{ marginTop: 6, fontSize: 13 }}>
        {error?.message || 'Unknown render error in ScheduleTargetSettings'}
      </div>
      <div style={{ marginTop: 8 }}>
        <button className="btn btn-sm btn-outline-secondary" onClick={resetErrorBoundary}>
          Retry
        </button>
      </div>
    </div>
  );
};

const ProviderDashboard = () => {
  const { user } = useSelector(state => state.auth);
  const practiceId = user?.practice_id;
  const { currentPractice } = useSelector(state => state.practice ?? {});

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  // Practice data from Redux store
  const [practiceData, setPracticeData] = useState(() => {
    if (!currentPractice) return null;
    return String(currentPractice.id) === String(practiceId) ? currentPractice : null;
  });
  const [loading, setLoading] = useState(() => {
    if (!currentPractice) return true;
    return String(currentPractice.id) !== String(practiceId);
  });
  const tabContentRef = useRef(null);

  // All tabs (including previously in More dropdown)
  const tabs = useMemo(() => ([
    { id: 'practice', label: 'Practice', icon: faUserMd },
    { id: 'admin', label: 'Admin', icon: faUsers },
    { id: 'provider', label: 'Provider', icon: faUserMd },
    { id: 'caregiver', label: 'Caregiver', icon: faUserNurse },
    { id: 'patient', label: 'Patient', icon: faUserInjured },
    // System Caregiver tab disabled
    // { id: 'system-caregiver', label: 'System Caregiver', icon: faUserCheck },
    { id: 'schedule', label: 'Schedule & Target setting', icon: faCalendarAlt },
    { id: 'import', label: 'Import Patients', icon: faFileImport }
  ]), []);

  const getTabFromSearch = useCallback((search) => {
    const params = new URLSearchParams(search);
    const requested = params.get('tab');
    return tabs.some(tab => tab.id === requested) ? requested : 'practice';
  }, [tabs]);

  const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));

  useEffect(() => {
    const nextTab = getTabFromSearch(location.search);
    setActiveTab(prev => (prev === nextTab ? prev : nextTab));
  }, [location.search, getTabFromSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('tab')) {
      params.set('tab', activeTab || 'practice');
      navigate({
        pathname: location.pathname,
        search: params.toString()
      }, { replace: true });
    }
  }, [location.search, activeTab, location.pathname, navigate]);

  useEffect(() => {
    const loadPracticeData = async () => {
      if (!practiceId) {
        setLoading(false);
        return;
      }
      try {
        // Reuse cached practice data if it matches this practiceId.
        if (currentPractice && String(currentPractice.id) === String(practiceId)) {
          setPracticeData(currentPractice);
          setLoading(false);
          return;
        }

        setLoading(true);
        const result = await dispatch(fetchPracticeSummary(practiceId));
        if (fetchPracticeSummary.fulfilled.match(result)) {
          setPracticeData(result.payload.data);
        } else {
          console.error('Failed to load practice:', result.payload);
        }
      } catch (error) {
        console.error('Error loading practice:', error);
      } finally {
        setLoading(false);
      }
    };

    if (practiceId) {
      loadPracticeData();
    }
  }, [practiceId, dispatch, currentPractice]);

  // Clear breadcrumbs when practice data loads
   useEffect(() => {
     if (practiceData) {
       clearBreadcrumbs();
     }
   }, [practiceData, clearBreadcrumbs]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  const handleTabChange = (tabId) => {
    console.debug('[ProviderDashboard] tab change requested ->', tabId);
    const params = new URLSearchParams(location.search);
    params.set('tab', tabId);
    params.delete('viewId'); // same as practice dashboard — avoid carrying provider/admin id into other tabs
    navigate({
      pathname: location.pathname,
      search: params.toString()
    }, { replace: true });
    setActiveTab(tabId);
  };

  const renderTabContent = () => {
    if (!practiceId) return null;

    switch (activeTab) {
      case 'practice':
        return <PracticeDetails practiceData={practiceData} />;
      case 'admin':
        return <AdminManagement practiceId={practiceId} />;
      case 'provider':
        return <ProviderManagement practiceId={practiceId} />;
      case 'caregiver':
        return <CaregiverManagement practiceId={practiceId} />;
      case 'patient':
        return <PatientManagement practiceId={practiceId} />;
      // System Caregiver disabled
      // case 'system-caregiver':
      //   return <SystemCaregiverManagement practiceId={practiceId} />;
      case 'schedule':
        // Minimal runtime-safety wrapper + force visible block to avoid CSS hiding
        return (
          <div style={{ display: 'block' }}>
            <LocalErrorBoundary FallbackComponent={ScheduleErrorFallback}>
              <ScheduleTargetSettings practiceId={practiceId} />
            </LocalErrorBoundary>
          </div>
        );
      case 'import':
        return <ImportPatients practiceId={practiceId} />;
      default:
        return <PracticeDetails practiceData={practiceData} />;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!practiceId) {
    return (
      <div className="alert alert-warning" role="alert">
        No practice associated with this provider account.
      </div>
    );
  }

  if (!practiceData) {
    // If loaded but no data
    return (
      <div className="alert alert-danger" role="alert">
        Practice not found!
      </div>
    );
  }

  return (
    <div className="provider-dashboard-feature">
      {/* Practice Header */}
      <div className="provider-dashboard-feature-header">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h2 className="provider-dashboard-feature-top-heading">{practiceData.practice_name}</h2>
          </div>
          <div className="provider-dashboard-feature-inner-logo d-flex flex-column align-items-end">
            {(() => {
              // Get logo URL - construct full URL if logo path exists
              const getLogoUrl = () => {
                if (!practiceData.practice_logo) return null;

                // If it's already a full URL, return it
                if (practiceData.practice_logo.startsWith('http://') || practiceData.practice_logo.startsWith('https://')) {
                  return practiceData.practice_logo;
                }

                // Otherwise, construct URL from backend
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
                // Remove /api if present in API_URL
                const baseUrl = API_URL.replace('/api', '');
                // Ensure logo path starts with /
                const logoPath = practiceData.practice_logo.startsWith('/')
                  ? practiceData.practice_logo
                  : `/${practiceData.practice_logo}`;

                return `${baseUrl}${logoPath}`;
              };

              const logoUrl = getLogoUrl();

              // Only show logo if practice has one
              if (logoUrl) {
                return (
                  <div className="provider-dashboard-feature-logo-container">
                    <div className="provider-dashboard-feature-logo">
                      <img
                        src={logoUrl}
                        alt={`${practiceData.practice_name || 'Practice'} Logo`}
                        className="provider-dashboard-feature-logo-image"
                        onError={(e) => {
                          // Hide image if it fails to load
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            <div className="provider-dashboard-feature-info-head mt-2">
              <span className="provider-dashboard-feature-title">Billing Period:</span>
              <span className="ms-2">
                {practiceData.rpm_start_date ?
                  new Date(practiceData.rpm_start_date).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                  }) : 'N/A'
                } ~ {practiceData.rpm_end_date ?
                  new Date(practiceData.rpm_end_date).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                  }) : 'N/A'
                }
              </span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-4">
          <ul className="provider-dashboard-feature-nav-tabs" role="tablist">
            {tabs.map((tab) => (
              <li key={tab.id} className="provider-dashboard-feature-nav-item" role="presentation">
                <button
                  className={`provider-dashboard-feature-nav-link ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                >
                  <FontAwesomeIcon icon={tab.icon} className="me-2" />
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content" ref={tabContentRef}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ProviderDashboard;
